import React from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('ru-RU');
};

const formatCoords = (lat, lng) => {
  const nLat = Number(lat);
  const nLng = Number(lng);
  if (!Number.isFinite(nLat) || !Number.isFinite(nLng)) return '-';
  return `${nLat.toFixed(6)}, ${nLng.toFixed(6)}`;
};

const AddressImportPanel = ({
  filters,
  onFilterChange,
  cities,
  districts,
  products,
  packagings,
  couriers,
  candidates,
  loading,
  importing,
  summary,
  onApplyFilters,
  onImport
}) => {
  const districtsByCity = filters.cityId
    ? districts.filter((district) => String(district?.city?._id || district?.city) === String(filters.cityId))
    : districts;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Импорт адресов с координатами
      </Typography>

      <Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        sx={{
          mb: 2,
          flexWrap: 'wrap',
          alignItems: 'flex-start'
        }}
      >
        <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 180px' }}>
          <InputLabel>Город</InputLabel>
          <Select
            label="Город"
            value={filters.cityId}
            onChange={(event) => onFilterChange('cityId', event.target.value)}
          >
            <MenuItem value="">Все города</MenuItem>
            {cities.map((city) => (
              <MenuItem key={city._id} value={city._id}>
                {city.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 180px' }}>
          <InputLabel>Район</InputLabel>
          <Select
            label="Район"
            value={filters.districtId}
            onChange={(event) => onFilterChange('districtId', event.target.value)}
          >
            <MenuItem value="">Все районы</MenuItem>
            {districtsByCity.map((district) => (
              <MenuItem key={district._id} value={district._id}>
                {district.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 180px' }}>
          <InputLabel>Товар</InputLabel>
          <Select
            label="Товар"
            value={filters.productId}
            onChange={(event) => onFilterChange('productId', event.target.value)}
          >
            <MenuItem value="">Все товары</MenuItem>
            {products.map((product) => (
              <MenuItem key={product._id} value={product._id}>
                {product.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 180px' }}>
          <InputLabel>Фасовка</InputLabel>
          <Select
            label="Фасовка"
            value={filters.packagingId}
            onChange={(event) => onFilterChange('packagingId', event.target.value)}
          >
            <MenuItem value="">Все фасовки</MenuItem>
            {packagings.map((packaging) => (
              <MenuItem key={packaging._id} value={packaging._id}>
                {packaging.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200, flex: '1 1 200px' }}>
          <InputLabel>Курьер</InputLabel>
          <Select
            label="Курьер"
            value={filters.courierId}
            onChange={(event) => onFilterChange('courierId', event.target.value)}
          >
            <MenuItem value="">Все курьеры</MenuItem>
            {couriers.map((courier) => (
              <MenuItem key={courier._id} value={courier._id}>
                {courier.name || courier.username || courier._id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Дата с"
          type="date"
          size="small"
          sx={{ minWidth: 160, flex: '1 1 160px' }}
          value={filters.dateFrom}
          onChange={(event) => onFilterChange('dateFrom', event.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Дата по"
          type="date"
          size="small"
          sx={{ minWidth: 160, flex: '1 1 160px' }}
          value={filters.dateTo}
          onChange={(event) => onFilterChange('dateTo', event.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Button variant="outlined" onClick={onApplyFilters} disabled={loading} sx={{ minWidth: 140 }}>
          {loading ? 'Загрузка...' : 'Применить'}
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={onImport}
          disabled={importing || loading || candidates.length === 0}
          sx={{ minWidth: 220 }}
        >
          {importing ? 'Импорт...' : 'Импортировать по фильтру'}
        </Button>
      </Stack>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Chip size="small" label={`Найдено: ${summary.total}`} />
        <Chip size="small" color="warning" label={`Уже импортированы: ${summary.importedAlreadyCount}`} />
        <Chip
          size="small"
          color="success"
          label={`К импорту: ${Math.max(summary.total - summary.importedAlreadyCount, 0)}`}
        />
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Город / Район</TableCell>
              <TableCell>Товар</TableCell>
              <TableCell>Курьер</TableCell>
              <TableCell>Дата заливки</TableCell>
              <TableCell>Координаты</TableCell>
              <TableCell>Статус</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {candidates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  Нет адресов под выбранные фильтры
                </TableCell>
              </TableRow>
            ) : (
              candidates.map((item) => (
                <TableRow key={item._id} hover>
                  <TableCell>{item.city?.name || '-'} / {item.district?.name || '-'}</TableCell>
                  <TableCell>{item.product?.name || '-'} / {item.packaging?.name || '-'}</TableCell>
                  <TableCell>{item.courier?.name || item.courier?.username || '-'}</TableCell>
                  <TableCell>{formatDate(item.createdAt)}</TableCell>
                  <TableCell>
                    {formatCoords(item.coordinateRecognition?.latitude, item.coordinateRecognition?.longitude)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.alreadyImported ? 'Уже импортирован' : 'Готов к импорту'}
                      color={item.alreadyImported ? 'default' : 'success'}
                      variant={item.alreadyImported ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AddressImportPanel;
